// AutomationRule.jsx

import React from 'react';

// CSS
import './AutomationRule.css';

// Components
import AutomationRuleActionsSection from './actions/AutomationRuleActionsSection';
import AutomationRuleDetails from './AutomationRuleDetails';
import AutomationRuleHeader from './AutomationRuleHeader';
import AutomationRuleConditionsSection from './conditions/AutomationRuleConditionsSection';

/**
 * AutomationRule
 *
 * Renders a single automation rule editor with trigger, conditions, and actions.
 *
 * @param {object} rule - The automation rule being edited.
 * @param {number} index - The display index of the rule in the list.
 * @param {array} fieldOptions - Available fields for action selection.
 * @param {array} ruleFieldOptions - Available fields for trigger/condition selection.
 * @param {object} fieldTypeByKey - Field type lookup keyed by field key.
 * @param {object} fieldEnumOptionsByKey - Enum options lookup keyed by field key.
 * @param {string} baseKey - Active base key for member recipient selection.
 * @param {array} nodeTypeOptions - Base node type options with people fields.
 * @param {function} updateRule - Updates a rule by key with an updater callback.
 * @param {function} handleRemoveRule - Removes a rule by key.
 * @param {function} handleAddCondition - Adds a condition row to a rule.
 * @param {function} handleRemoveCondition - Removes a condition row from a rule.
 * @param {function} updateConditionRows - Updates condition rows for a rule.
 * @param {function} handleAddAction - Adds an action row to a rule.
 * @param {function} handleRemoveAction - Removes an action row from a rule.
 * @param {boolean} [allowEmailActions=true] - Whether send-email actions can be selected.
 * @param {boolean} [allowCollapse=true] - Whether the rule can collapse in place.
 * @param {boolean} [showHeader=true] - Whether to render the rule header.
 * @param {boolean} [showRemoveButton=true] - Whether to render the remove button in the header.
 * @param {string} [title] - Optional header title override.
 *
 * @returns {ReactNode} The rendered automation rule.
 */
const AutomationRule = React.memo(({
    rule,
    index,
    fieldOptions,
    ruleFieldOptions,
    fieldTypeByKey,
    fieldEnumOptionsByKey,
    baseKey,
    nodeTypeOptions,
    updateRule,
    handleRemoveRule,
    handleAddCondition,
    handleRemoveCondition,
    updateConditionRows,
    handleAddAction,
    handleRemoveAction,
    allowEmailActions = true,
    allowCollapse = true,
    showHeader = true,
    showRemoveButton = true,
    title = '',
}) => {

    // ----- STATE ----- //

    // Collapsed State
    const [isCollapsed, setIsCollapsed] = React.useState(false);

    // ----- UTILITIES ----- //

    // Rule Actions
    const actions = Array.isArray(rule.actions) ? rule.actions : [];

    const resolvedTitle = String(title || '').trim() || `Rule ${index + 1}`;
    const resolvedCollapsed = allowCollapse ? isCollapsed : false;

    // ----- RENDER ----- //

    return (
        <div className="automation-rule-container">

            {showHeader && (
                <AutomationRuleHeader
                  ruleKey={rule.key}
                  title={resolvedTitle}
                  isCollapsed={isCollapsed}
                  allowCollapse={allowCollapse}
                  showRemoveButton={showRemoveButton}
                  onRemove={handleRemoveRule}
                  onToggleCollapse={() => setIsCollapsed((prev) => !prev)}
                />
            )}

            <AutomationRuleDetails
              rule={rule}
              ruleFieldOptions={ruleFieldOptions}
              isCollapsed={resolvedCollapsed}
              updateRule={updateRule}
            />

            {!resolvedCollapsed && (
                <>
                    <AutomationRuleConditionsSection
                      rule={rule}
                      fieldOptions={ruleFieldOptions}
                      fieldTypeByKey={fieldTypeByKey}
                      fieldEnumOptionsByKey={fieldEnumOptionsByKey}
                      updateConditionRows={updateConditionRows}
                      handleRemoveCondition={handleRemoveCondition}
                      handleAddCondition={handleAddCondition}
                    />

                    <AutomationRuleActionsSection
                      rule={rule}
                      actions={actions}
                      fieldOptions={fieldOptions}
                      fieldTypeByKey={fieldTypeByKey}
                      fieldEnumOptionsByKey={fieldEnumOptionsByKey}
                      baseKey={baseKey}
                      nodeTypeOptions={nodeTypeOptions}
                      allowEmailActions={allowEmailActions}
                      updateRule={updateRule}
                      handleRemoveAction={handleRemoveAction}
                      handleAddAction={handleAddAction}
                    />
                </>
            )}

        </div>
    );
});

// Dev Tools
AutomationRule.displayName = 'AutomationRule';

export default AutomationRule;
